<template>
  <div class="campaigns-reports-container">
    <C2wCampaignsFilters
      ref="filters"
      :campaignsData="campaigns"
      @filter-change="onFilterChange"
    />
    <div class="reports-wrap">
      <C2wCampaignsSummary :messages="filteredCampaigns" />
      <div class="bar-section-group">
        <div class="bar-section">
          <h5 class="chart-title">
            {{ $t('C2W_CAMPAIGNS_REPORT.CHART.CLICKS_BY_DATE') }}
          </h5>
          <C2wClicksByDateBarChart :campaigns="filteredCampaigns" />
        </div>

        <div class="bar-section">
          <h5 class="chart-title">
            {{ $t('C2W_CAMPAIGNS_REPORT.CHART.CLICKS_BY_TITLE') }}
          </h5>
          <C2wClicksByTitleBarChart :campaigns="filteredCampaigns" />
        </div>
      </div>
      <div class="table-section no-bg">
        <C2wCampaignsTable :campaignsData="filteredCampaigns" />
      </div>
    </div>
  </div>
</template>

<script>
import C2wCampaignsTable from './components/C2wCampaignsTable';
import C2wCampaignsFilters from './components/C2wCampaignsFilters';
import C2wCampaignsReportApi from '../../../../api/inbox/historyC2wCampaigns';
import C2wClicksByDateBarChart from './components/C2wClicksByDateBarChart';
import C2wClicksByTitleBarChart from './components/C2wClicksByTitleBarChart';
import C2wCampaignsSummary from './components/C2wCampaignsSummary';

export default {
  name: 'C2wCampaignsReportsView',
  components: {
    C2wCampaignsFilters,
    C2wCampaignsTable,
    C2wCampaignsSummary,
    C2wClicksByDateBarChart,
    C2wClicksByTitleBarChart,
  },
  data() {
    return {
      campaigns: [],
      filters: {
        customDateRange: '',
        sources: [],
        sourceUrls: [],
        headlines: [],
      },
    };
  },
  computed: {
    filteredCampaigns() {
      const [start, end] = this.filters.customDateRange || [];

      const filtered = this.campaigns.filter(item => {
        const createdDate = new Date(item.created_at);

        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        if (startDate) {
          startDate.setHours(0, 0, 0, 0);
        }

        if (endDate) {
          endDate.setHours(23, 59, 59, 999);
        }

        const matchDate =
          (!startDate || createdDate >= startDate) &&
          (!endDate || createdDate <= endDate);

        const matchSource =
          !this.filters.sources?.length ||
          this.filters.sources.includes(item.source);

        const matchSourceUrl =
          !this.filters.sourceUrls?.length ||
          this.filters.sourceUrls.includes(item.source_url);

        const matchHeadline =
          !this.filters.headlines?.length ||
          this.filters.headlines.includes(item.headline);

        const matchInbox =
          !this.filters.inboxes?.length ||
          this.filters.inboxes.includes(item.inbox_name);

        return (
          matchDate &&
          matchSource &&
          matchSourceUrl &&
          matchHeadline &&
          matchInbox
        );
      });

      return filtered.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.filters.emitFilters();
    });
  },
  methods: {
    prepareCampaigns(rawCampaigns) {
      if (!Array.isArray(rawCampaigns)) return [];

      return rawCampaigns.map(campaign => {
        const statusMap = {
          sent: this.$t('C2W_CAMPAIGNS_REPORT.STATUS.SENT'),
          delivered: this.$t('C2W_CAMPAIGNS_REPORT.STATUS.DELIVERED'),
          read: this.$t('C2W_CAMPAIGNS_REPORT.STATUS.READ'),
          failed: this.$t('C2W_CAMPAIGNS_REPORT.STATUS.FAILED'),
        };

        const status_message =
          statusMap[campaign.status] || this.$t('C2W_CAMPAIGNS_REPORT.UNKNOWN');

        const message_error =
          campaign.content_attributes?.external_error || '-';

        const interacted =
          new Date(campaign.last_activity_at) >= new Date(campaign.created_at)
            ? this.$t('C2W_CAMPAIGNS_REPORT.INTERACTION.YES')
            : this.$t('C2W_CAMPAIGNS_REPORT.INTERACTION.NO');

        const link_conversation = `${window.location.origin}/app/accounts/${campaign.account_id}/conversations/${campaign.display_id}`;

        return {
          ...campaign,
          status_message,
          message_error,
          interacted,
          link_conversation,
        };
      });
    },
    async onFilterChange(newFilters) {
      this.filters = {
        ...this.filters,
        ...newFilters,
      };

      try {
        const [start, end] = this.filters.customDateRange || [];

        const formatDate = date => {
          const d = new Date(date);

          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');

          return `${year}-${month}-${day}`;
        };

        const response = await C2wCampaignsReportApi.getAll({
          startDate: start ? formatDate(start) : null,
          endDate: end ? formatDate(end) : null,
        });

        const campaigns = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        this.campaigns = this.prepareCampaigns(campaigns);
      } catch (error) {
        console.error(this.$t('C2W_CAMPAIGNS_REPORT.ERRORS.FETCH'), error);
      }
    },
  },
};
</script>

<style scoped>
.campaigns-reports-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  align-items: flex-start;
}

.campaigns-reports-container > *:first-child {
  width: 280px;
  flex-shrink: 0;
  margin-right: 24px;
}

.reports-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 2vh 2vw 10vh;
  background: #f8fafc;
  overflow: hidden;
  min-width: 0;
  box-sizing: border-box;
}

.bar-section,
.table-section {
  margin-top: 4vh;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  width: 100%;
  overflow-x: auto;
}

.table-section.no-bg {
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.bar-section-group {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 1vh;
}

.bar-section-group .bar-section {
  flex: 1 1 48%;
  max-height: 500px;
  overflow: hidden;
  background: #fff;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.05);
}

.chart-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
}
</style>
